import { createStrictOptionalContext } from 'helpers/context';
import type { IssueReferenceName } from 'shared/domain/issue/issueReferences';
import { FieldSkeleton } from 'shared/types/form';
import { IssueOnSingleView } from './IssueOnSingleView';

/** The fields of the current issue's form; generated once, by `WithIssue`. */
export interface CurrentIssueForm {
  primaryFields: FieldSkeleton[];
  extendedFields: FieldSkeleton[];
  /** The form of the issue can be shown: its process and fields are ready. */
  formReady: boolean;
}

const {
  Provider: CurrentIssueProvider,
  /** The current issue as the issue view shows it; `undefined` until the data it names is ready. */
  useRequiredContext: useCurrentIssue,
} = createStrictOptionalContext<IssueOnSingleView | undefined>('useCurrentIssue', 'WithIssue');
const { Provider: CurrentIssueFormProvider, useRequiredContext: useCurrentIssueForm } =
  createStrictOptionalContext<CurrentIssueForm>('useCurrentIssueForm', 'WithIssue');

const {
  Provider: CurrentIssueMissingReferenceProvider,
  /** The site, level or process the current issue refers to that does not exist; the view cannot show the issue. */
  useRequiredContext: useCurrentIssueMissingReference,
} = createStrictOptionalContext<IssueReferenceName | undefined>('useCurrentIssueMissingReference', 'WithIssue');

export {
  CurrentIssueFormProvider,
  CurrentIssueMissingReferenceProvider,
  CurrentIssueProvider,
  useCurrentIssue,
  useCurrentIssueForm,
  useCurrentIssueMissingReference,
};

/** The issue the issue view shows: its components render only once it is there. */
export function useShownIssue(): IssueOnSingleView {
  const issue = useCurrentIssue();
  if (!issue) {
    throw new Error('useShownIssue must be used once the issue view shows the issue');
  }
  return issue;
}
